import React, { useEffect, useRef, useState } from 'react';
import { AppLink, navigate } from 'mydish-ui-util';
import { useHistory } from 'react-router-dom';
import '../css/global-nav.sass';
import '../css/top-nav.sass';
// @ts-ignore
import caratRed from '../css/assets/img/carat-red.svg';
// @ts-ignore
import dishLogo from '../css/assets/img/unifiedNavLogo.svg';
import CampfireButton from './CampfireButton';
import NavButton from './NavButton';
import FloatingSearchInput from './FloatingSearchInput';
import Anchor from './Anchor';
import { Navigation } from './TopNavigation';
import HoverNavList from './HoverNavList';

export interface SelectedNavigation {
  topNav: string;
  drawerNav: string;
}

export interface TopNavProps {
  theme?: 'night' | 'day';
  appName?: string;
  searchUrl?: string;
  pageContentId?: string;
  skipToContentText?: string;
  searchLabel?: string;
  navigations: Navigation[];
  onSignIn?: Function;
  onSignOut?: Function;
  displaySignInOutIcon?: boolean;
  signOutUrl?: string;
  signOutLabel?: string;
  signInUrl?: string;
  signInLabel?: string;
  isLoggedIn?: boolean;
  searchIcon?: string;
  logo?: string;
  autoSuggest?: boolean;
  suggestOn?: number;
  maxSuggestions?: number;
  maxLength?: number;
  queryParam?: string;
}

interface TopNavState {
  selectedNavName?: string;
  navSelected: boolean;
  displaySearchInput: boolean;
  searchText?: string;
  toggleMenuOpen: boolean;
  defaultNavName: string;
  selectedNavId: string;
}

export default function TopNav(props: TopNavProps) {
  const {
    searchIcon = '/cms-files/common/icon-search-04.png',
    pageContentId = 'pageContent',
    skipToContentText = 'Skip to content',
    appName = 'MyDISH',
    signOutLabel = 'Sign Out',
    signInLabel = 'Sign In',
    theme = 'night',
    logo = dishLogo
  } = props;

  const history = useHistory();

  const signInOutUrl = props.isLoggedIn ? props.signOutUrl : props.signInUrl;
  const signInOutLabel = props.isLoggedIn ? signOutLabel : signInLabel;
  const displaySignInOut = !!signInOutUrl || props.onSignOut || props.onSignIn;

  const [state, setState] = useState<TopNavState>({
    navSelected: false,
    displaySearchInput: false,
    toggleMenuOpen: false,
    defaultNavName: '',
    selectedNavId: ''
  });

  const [isHamburgerActive, setHamburgerActive] = useState<boolean>(false);
  const [isSearchOpen, setSearchOpen] = useState<boolean>(false);
  const [closeIconWasLastFocused, setCloseIconWasLastFocused] = useState(false);
  const [submenuMousePostion, setSubMenuMousePosition] = useState(0);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 992);

  const signinIconRef = useRef<HTMLButtonElement | null>(null);
  const closeIconRef = useRef<HTMLButtonElement | null>(null);
  const searchBtnIconRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setState({
      ...state,
      navSelected: false
    });
  }, [props]);

  const onWindowResize = () => {
    if (window.innerWidth < 992) {
      setIsMobileView(true);
    } else setIsMobileView(false);
  };

  useEffect(() => {
    window.addEventListener('resize', onWindowResize, true);
    return () => {
      window.removeEventListener('resize', onWindowResize, true);
    };
  }, []);

  useEffect(() => {
    function handleKeyDown(e: any) {
      if (e.key === 'Tab') {
        if (document.activeElement === closeIconRef.current) {
          e.preventDefault();
          signinIconRef?.current?.focus();
          setCloseIconWasLastFocused(true);
          setSearchOpen(false);
        } else if (
          document.activeElement === signinIconRef.current &&
          closeIconWasLastFocused
        ) {
          setCloseIconWasLastFocused(false);
        }
      }
      if (e.key === 'Enter') {
        if (document.activeElement === closeIconRef.current) {
          e.preventDefault();
          setSearchOpen(false);
          searchBtnIconRef?.current?.focus();
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeIconWasLastFocused]);

  const toggleHamburgerMenu = () => {
    setHamburgerActive(!isHamburgerActive);
    setSearchOpen(false);
    setState({
      ...state,
      selectedNavName: '',
      toggleMenuOpen: false
    });
  };

  const onTopNavOptionLinkClick = (
    view: string,
    index: number,
    isActiveNav: boolean,
    navName: string
  ) => {
    setState({
      ...state,
      selectedNavName: isActiveNav ? '' : navName,
      navSelected: !state.navSelected,
      selectedNavId: isActiveNav ? '' : `${view}-nav-link-${index}`
    });
  };

  const mobileAccountIcon = displaySignInOut && (
    <button
      ref={signinIconRef}
      type="button"
      className={`accountIcon ${props.displaySignInOutIcon ? 'signInIcon' : ''}
    `}
      aria-label="Account Icon"
      onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
      }}
      onClick={() => {
        if (props.isLoggedIn && props.onSignOut) {
          props.onSignOut();
        } else if (!props.isLoggedIn && props.onSignIn) {
          props.onSignIn();
        } else if (signInOutUrl) {
          navigate(`${signInOutUrl}`, history);
        }
      }}
    >
      <div
        className={`accountIcon ${
          props.displaySignInOutIcon ? 'signInIcon' : ''
        }`}
      />
    </button>
  );

  let keyIndex = 0;
  const navOptions = (view: string) => {
    return props.navigations.map((navigation: Navigation, index: number) => {
      const navName = navigation.name || navigation.pageName;
      keyIndex += 1;
      const isActiveNav = state.selectedNavName === navName;
      return (
        <li
          key={`${navName}${keyIndex}`}
          className={`topnav-options-tab ${
            state.displaySearchInput ? ' hidden' : ''
          }${isActiveNav ? ' active' : ''}`}
          id={`${view}-nav-option-${index}`}
          onMouseEnter={() => {
            if (!isMobileView) {
              setState({
                ...state,
                selectedNavName: navigation.name,
                toggleMenuOpen: true
              });
              document.body.style.overflow = 'hidden';
            }
          }}
          onMouseLeave={(e) => {
            if (!isMobileView) {
              if (e.screenY !== submenuMousePostion)
                setState({
                  ...state,
                  selectedNavName: '',
                  toggleMenuOpen: false
                });
            }
            document.body.style.removeProperty('overflow');
          }}
        >
          <div className="topnav-options-header">
            <NavButton
              id={`${view}-nav-link-${index}`}
              className={`topnav-options-link${isActiveNav ? ' active' : ''}`}
              active={isActiveNav}
              label={navName}
              ariaExpanded={
                state.navSelected && state.selectedNavName === navName
              }
              onClick={() =>
                onTopNavOptionLinkClick(view, index, isActiveNav, navName)
              }
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setState({
                    ...state,
                    selectedNavName: !isActiveNav ? '' : navName,
                    navSelected: !state.navSelected,
                    selectedNavId: !isActiveNav
                      ? ''
                      : `${view}-nav-link-${index}`
                  });
                }
              }}
            />
            {isMobileView && (
              <button
                id={`${view}-nav-img-${index}`}
                onClick={() =>
                  onTopNavOptionLinkClick(view, index, isActiveNav, navName)
                }
                type="button"
              >
                <img
                  src={caratRed}
                  className="topnav-mobile-carat"
                  alt="More information icon"
                />
              </button>
            )}
          </div>
          <HoverNavList
            sectionPages={navigation.sectionPages}
            childPages={navigation.childPages}
            active={isActiveNav}
            setSubMenuMousePosition={setSubMenuMousePosition}
            navIndex={index}
            history={history}
          />
        </li>
      );
    });
  };

  const handleSearch = () => {
    setHamburgerActive(false);
    setSearchOpen(!isSearchOpen);
  };

  const handleAnchorTagClick = (anchorTag: Element | null) => {
    if (anchorTag instanceof window.HTMLElement) {
      anchorTag.click();
    }
  };

  return (
    <>
      <div
        className="skipNav"
        tabIndex={0}
        onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
          e.preventDefault();
          const anchorTag = e.currentTarget.firstElementChild;
          handleAnchorTagClick(anchorTag);
        }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            const anchorTag = e.currentTarget.firstElementChild;
            handleAnchorTagClick(anchorTag);
          }
        }}
        role="button"
      >
        <Anchor
          arialabel="skipToContent"
          aria-label="skipToContent"
          id="skipToContent"
          href={`#${pageContentId}`}
          tabIndex={-1}
        >
          {skipToContentText}
        </Anchor>
      </div>
      <nav className="topnav">
        <div className="topnav-left">
          <AppLink href="/" className="dishlogo">
            <img
              alt={`${appName} homepage`}
              src={logo}
              title="Go to homepage"
            />
          </AppLink>
          <div className="topnav-options">
            <ul
              className={`topnav-options-list ${
                isHamburgerActive ? 'hamActive' : ''
              }`}
            >
              {navOptions('menu')}
            </ul>
          </div>
        </div>
        <div className="topnav-right">
          <button
            ref={searchBtnIconRef}
            type="button"
            className={`topnav-search-toggle ${
              isSearchOpen ? 'search-active' : ''
            }`}
            onClick={handleSearch}
          >
            <img src={searchIcon} alt="search icon" title="Search DISH" />
          </button>
          {displaySignInOut && (
            <div className="nav-sign-in">
              {mobileAccountIcon}
              <span className="signOutBtn">
                {!props.displaySignInOutIcon && (
                  <CampfireButton
                    name="button"
                    id="signInOutButton"
                    label={signInOutLabel}
                    color="coal"
                    ghost
                    smol
                    theme={theme}
                    onClick={() => {
                      if (props.isLoggedIn && props.onSignOut) {
                        props.onSignOut();
                      } else if (!props.isLoggedIn && props.onSignIn) {
                        props.onSignIn();
                      } else if (signInOutUrl) {
                        navigate(`${signInOutUrl}`, history);
                      }
                    }}
                  />
                )}
              </span>
            </div>
          )}
          {isMobileView && (
            <button
              type="button"
              aria-haspopup="true"
              aria-controls="mainMenuBar"
              aria-label="Mobile Menu"
              className={`nav-hamburger ${isHamburgerActive ? 'active' : ''}`}
              onClick={toggleHamburgerMenu}
              tabIndex={-1}
            >
              <span />
              <span>Menu</span>
              <span />
            </button>
          )}
        </div>
        <div
          className={`topnav-search ${
            isSearchOpen ? 'opacity-1' : 'opacity-0'
          }`}
          aria-hidden={!isSearchOpen}
        >
          <FloatingSearchInput
            suggestOn={props.suggestOn}
            maxSuggestions={props.maxSuggestions}
            maxLength={props.maxLength}
            iconSrc={props.searchIcon}
            queryParam={props.queryParam}
            autoSuggest={props.autoSuggest}
            name="globalSearch"
            id="globalSearch"
            theme={theme}
            handleClose={handleSearch}
            isActive={isSearchOpen}
            focus={isSearchOpen}
            searchInputLabel={props.searchLabel}
            onSearch={(searchText: string) => {
              navigate(
                `${props.searchUrl?.replace('{searchText}', searchText)}`,
                history
              );
            }}
            onBlur={(e: any) => {
              setState({
                ...state,
                searchText: e.target.value,
                displaySearchInput: false
              });
            }}
            closeIconRef={closeIconRef}
            disabled={!isSearchOpen}
          />
        </div>
      </nav>
    </>
  );
}
